import React, { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { t } from 'i18next';
import { Trans } from 'react-i18next';
import { useMediaQuery } from 'react-responsive';
import {
  ResultModalWrapper,
  Result,
  InfoContaner,
  Status,
  HeaderInfo,
  TextInfo,
  CTABlock,
  Vehicle,
  ImageWrapper,
  VehicleImage,
  VehicleInfo,
  ModelName,
  Trimline,
  CTA,
  IconWrapper,
  ContactInfo,
  PhoneNumber,
  CTAredirect,
} from './ResultModal.styles';
import FallbackCarDefault from '../../assets/fallback_car_default.png';
import { selectAppConfig } from '../../redux/appConfigReducer';
import { selectDealerAndVehicleData } from '../../redux/dealerAndVehicleReducer';
import { selectForm } from '../../redux/formReducer';
import SuccessIcon from '../../assets/icons/SuccessIcon';
import CancelIcon from '../../assets/icons/CancelIcon';
import CautionIcon from '../../assets/icons/CautionIcon';
import { CloseModal } from '../DitigalLeads.styles';
import MailIcon from '../../assets/icons/MailIcon';
import { device } from '../../styles/devices';
import { Spinner } from '../Submit/Submit.styles';
import { FEATURE_APP_DETAILS } from '../../constants';
import { convertToPromotionsType } from '../../functions/convertToPromotionsType';
import { digitalLeadsFormSubmissionAttempt } from '../../utils/analyticsEventListeners';
import { useElspFormSubmission, useCAFormSubmission } from '../../hooks/useFormSubmission';
import { setError, setLeadId } from '../../redux/localConfigReducer';
import PhoneIcon from '../../assets/icons/PhoneIcon';
import phoneNumberFormatter from '../../functions/phoneNumberFormatter';
import { getVehicleForPromotions } from '../../functions/getVehicleForPromotions';
import useSourceOfLead from '../../hooks/useSourceOfLead';

export interface ResultModalProps {
  isSuccess: boolean;
  handleCloseModal: () => void;
}

const ResultModal: React.FC<ResultModalProps> = ({ isSuccess, handleCloseModal }) => {
  const [loading, setLoading] = useState(false);

  const { presetForm, isOfferPage, offers, market, customPhoneNumber, successModalConfig } =
    useSelector(selectAppConfig);
  const { vehicle, dealer } = useSelector(selectDealerAndVehicleData);
  const contactForm = useSelector(selectForm);
  const promotion = convertToPromotionsType(offers!);
  const { generalSource, specificSource } = useSourceOfLead();

  const dispatch = useDispatch();
  const handleElspSubmit = useElspFormSubmission();
  const handleCigsSubmit = useCAFormSubmission();
  const isUSMarket = market?.toLowerCase().includes('us');
  const isSubmitToCigs =
    !isUSMarket &&
    generalSource === 'tier_1' &&
    specificSource !== 'vlp' &&
    specificSource !== 'vdp';

  const isLaptop = useMediaQuery({ query: device.laptop });
  const isTablet = useMediaQuery({ query: device.tablet });
  const isMobile = useMediaQuery({ query: device.mobileS });

  const isMobileScreen = isMobile && !isTablet && !isLaptop;

  const image = useMemo(() => {
    if (isOfferPage && offers?.image) return offers.image;
    if (vehicle?.images[0]?.url) return vehicle?.images[0]?.url;
    return FallbackCarDefault;
  }, [isOfferPage, offers?.image, vehicle?.images]);

  const modelName = vehicle?.titleText;
  const trimName = vehicle?.subtitleText?.replace('HP', '');

  const ctaTextLabel = t('digitalLeads.tryAgain');
  const submitElement = {
    ctaText: ctaTextLabel,
    ctaType: 'submit',
    urlLink: '',
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const vehicleData = !vehicle && offers ? getVehicleForPromotions(offers) : vehicle;

    digitalLeadsFormSubmissionAttempt(
      contactForm,
      vehicleData,
      FEATURE_APP_DETAILS,
      promotion,
      submitElement,
    );

    setLoading(true);
    const response = isSubmitToCigs ? await handleCigsSubmit() : await handleElspSubmit();
    setLoading(false);
    const { leadId, error } = response;
    if (leadId) return dispatch(setLeadId(leadId));

    let errorMessage = {
      code: 0,
      message: '',
    };
    if (error?.message) {
      if (error?.isConfigError) {
        const [message] = error.message;

        errorMessage.code = 0;
        errorMessage.message = message;
      } else {
        [errorMessage] = error.message;
      }
    }
    return dispatch(setError(errorMessage));
  };

  const ctaVerbiage = successModalConfig.successCustomCtaVerbiage || t('digitalLeads.returnToPage');

  const statusMessage = useMemo(() => {
    // 1st condition if success - show it
    // default - fail message
    if (isSuccess)
      return (
        <Status data-testid="result-modal-status-success">
          <SuccessIcon size={isLaptop ? 24 : 16} />
          {t('digitalLeads.status.success.status')}
        </Status>
      );

    return (
      <Status data-testid="result-modal-status-error">
        <CautionIcon size={isLaptop ? 24 : 16} />
        {t('digitalLeads.status.error.status')}
      </Status>
    );
  }, [isLaptop, isSuccess]);

  const isGeneralForm = presetForm === 'general' || (presetForm === 'tradeIn' && !vehicle);

  const statusText = useMemo(() => {
    if (isSuccess && successModalConfig.successCustomBody)
      return successModalConfig.successCustomBody;

    if (isSuccess)
      return (
        <Trans
          i18nKey="digitalLeads.status.success.message"
          values={{ dealerName: dealer?.name }}
          components={{ 1: <b /> }}
        />
      );
    return (
      <Trans
        i18nKey="digitalLeads.status.error.message"
        values={{ dealerName: dealer?.name }}
        components={{ 1: <b /> }}
      />
    );
  }, [dealer?.name, isSuccess, successModalConfig.successCustomBody]);

  const statusHeader = useMemo(() => {
    if (isSuccess && successModalConfig.successCustomHeading)
      return successModalConfig.successCustomHeading;
    if (isSuccess) return <Trans i18nKey="digitalLeads.status.success.header" />;
    return <Trans i18nKey="digitalLeads.status.error.header" />;
  }, [isSuccess, successModalConfig.successCustomHeading]);

  const contactInfo = useMemo(() => {
    // if no error - return null
    if (isSuccess) return null;
    if (!dealer?.phone || !customPhoneNumber) return null;

    const dealerPhone = customPhoneNumber
      ? phoneNumberFormatter(customPhoneNumber)
      : phoneNumberFormatter(dealer?.phone);

    return (
      <ContactInfo data-testid="result-modal-contact-info">
        <PhoneIcon />
        <PhoneNumber href="tel:">{dealerPhone}</PhoneNumber>
      </ContactInfo>
    );
  }, [customPhoneNumber, dealer?.phone, isSuccess]);

  const vehicleInfo = useMemo(() => {
    if (isGeneralForm) return null;
    if (isMobileScreen) return null;
    return (
      <Vehicle>
        <ImageWrapper>
          <VehicleImage src={image} />
        </ImageWrapper>
        <VehicleInfo>
          <ModelName>{modelName}</ModelName>
          <Trimline>{trimName}</Trimline>
        </VehicleInfo>
      </Vehicle>
    );
  }, [image, isGeneralForm, isMobileScreen, modelName, trimName]);

  const ReturnCta = useMemo(() => {
    if (successModalConfig.successCustomCtaUrl)
      return (
        <CTAredirect
          isPrimary
          isGeneralForm={isGeneralForm}
          href={successModalConfig.successCustomCtaUrl}
          data-testid="result-modal-return-cta"
        >
          {ctaVerbiage}
        </CTAredirect>
      );
    return (
      <CTA
        isPrimary
        isGeneralForm={isGeneralForm}
        onClick={handleCloseModal}
        data-testid="result-modal-return-cta"
      >
        {ctaVerbiage}
      </CTA>
    );
  }, [ctaVerbiage, handleCloseModal, isGeneralForm, successModalConfig.successCustomCtaUrl]);

  return (
    <ResultModalWrapper data-testid="result-modal-wrapper">
      <CloseModal onClick={handleCloseModal} isResultModal data-testid="result-modal-close-modal">
        <CancelIcon />
      </CloseModal>
      <InfoContaner>
        <Result>
          {statusMessage}
          <HeaderInfo data-testid="result-modal-header" isGeneralForm={isGeneralForm}>
            {statusHeader}
          </HeaderInfo>
          <TextInfo data-testid="result-modal-status-text">{statusText}</TextInfo>
          {contactInfo}
          <CTABlock isGeneralForm={isGeneralForm}>
            {ReturnCta}
            {/* Here second button without isPrimary for error + functionality to re-send */}
            {!isSuccess && (
              <CTA
                isGeneralForm={isGeneralForm}
                onClick={onSubmit}
                data-testid="result-modal-try-again-cta"
              >
                {loading ? <Spinner /> : ctaTextLabel}
              </CTA>
            )}
          </CTABlock>
        </Result>
        {vehicleInfo}
      </InfoContaner>
    </ResultModalWrapper>
  );
};

export default ResultModal;
